import { cloneDeep, omit } from 'lodash-es'
import { getCurrentRegion } from '@/utils/storage'
import { userData } from './common'

export const subMenuList = [
  'dashboard',
  'controlpanel',
  'devicelist',
  'templatelist',
  'devicegroup',
  'projectlist',
  'myAccount',
  'rechargeRecord',
  'msgBilling',
  'firmware',
  'user',
  'role',
  'apiManage',
  'safeZone',
] as const

export type MenuType = 'operations' | 'device' | 'billing' | 'message' | 'maintenance' | 'system'
export type SubMenuType = (typeof subMenuList)[number]

type PermissionOperations = Record<string, string>
type PermissionMenu = Partial<Record<SubMenuType, PermissionOperations>>
type PermissionMapType = Record<MenuType, PermissionMenu>

export const DIAComPm = 45
export const permissionMap: PermissionMapType = {
  //数据看板
  operations: {
    dashboard: {
      1: 'dashboard-view',
    },
    controlpanel: {
      46: 'controlpanel-view',
    },
  },
  //设备管理
  device: {
    devicelist: {
      2: 'devicelist-view',
      3: 'devicelist-bind',
      4: 'devicelist-edit',
      5: 'devicelist-delete',
      6: 'devicelist-hmi',
      7: 'devicelist-config',
      // 44: "devicelist-alarm"
      47: 'devicelist-monitor',
      48: 'devicelist-hmi-check',
      50: 'devicelist-alloc', // 设备列表-分配权限
    },
    templatelist: {
      8: 'templatelist-view',
      9: 'templatelist-add',
      10: 'templatelist-copy',
      11: 'templatelist-edit',
      12: 'templatelist-delete',
      36: 'templatelist-data-pm',
    },
    devicegroup: {
      13: 'devicegroup-view',
      14: 'devicegroup-add',
      15: 'devicegroup-edit',
      16: 'devicegroup-delete',
      37: 'devicegroup-data-pm',
    },
    projectlist: {
      39: 'projectlist-view',
      40: 'projectlist-add',
      42: 'projectlist-edit',
      43: 'projectlist-delete',
      41: 'projectlist-data-pm',
    },
  },
  //计费管理
  billing: {
    myAccount: {
      17: 'myAccount-view',
      18: 'myAccount-ca-charge',
      19: 'myAccount-ca-allcoate',
      20: 'myAccount-ra-charge',
      21: 'myAccount-ra-allcoate',
    },
    rechargeRecord: {
      22: 'rechargeRecord-view',
    },
  },
  //消息中心
  message: {
    msgBilling: {
      23: 'msgBilling-view',
    },
    // profile: "个人中心",
  },
  //运维管理
  maintenance: {
    firmware: {
      51: 'firmware-view',
      52: 'firmware-upload',
      53: 'firmware-auth',
      54: 'firmware-delete',
      55: 'firmware-dispatch',
    },
  },
  //系统管理
  system: {
    user: {
      24: 'user-view',
      25: 'user-invite',
      26: 'user-edit',
      27: 'user-delete',
      38: 'user-data-pm',
    },
    role: {
      28: 'role-view',
      29: 'role-add',
      31: 'role-edit',
      32: 'role-delete',
    },
    apiManage: {
      56: 'apiManage-view',
      57: 'apiManage-add',
      58: 'apiManage-edit',
      59: 'apiManage-delete',
      60: 'apiManage-download',
      61: 'apiManage-reset',
    },

    safeZone: {
      33: 'safeZone-view',
      30: 'safeZone-add',
      34: 'safeZone-edit',
      35: 'safeZone-delete',
    },
  },
}

/**
 * @brief 获取菜单下指定子菜单的操作权限映射。
 */
function getPermissionOperations(menu: MenuType, subMenu: SubMenuType): PermissionOperations | undefined {
  return permissionMap[menu][subMenu]
}

/**
 * @brief 按既有规则排序操作权限，查看权限在前，HMI 相关权限在后。
 */
function getSortedOperationIds(operations: PermissionOperations): string[] {
  return Object.keys(operations).sort((left, right) => {
    const leftOperation = operations[left]
    const rightOperation = operations[right]
    const isLeftHmi = leftOperation === 'devicelist-hmi' || leftOperation === 'devicelist-hmi-check'
    const isRightHmi = rightOperation === 'devicelist-hmi' || rightOperation === 'devicelist-hmi-check'
    const isLeftView = leftOperation.includes('view')
    const isRightView = rightOperation.includes('view')

    if (isLeftHmi !== isRightHmi) return isLeftHmi ? 1 : -1
    if (isLeftView !== isRightView) return isLeftView ? -1 : 1
    return 0
  })
}

//一级菜单数组
export const getMenuList = (): MenuType[] => {
  let permissions: Partial<PermissionMapType> = cloneDeep(permissionMap)
  // console.log("permissionMap", permissionMap)
  const region = getCurrentRegion()
  // console.log("region", region)
  if (region === 'cn') {
    permissions = omit(permissions, ['billing', 'message'])
  }
  // console.log("billing", permissions)
  return Object.keys(permissions) as MenuType[]
}

//二级菜单数组
export const getSubMenuList = (): readonly SubMenuType[] => subMenuList

//三级菜单对象集合
export const getOpList = (): PermissionOperations => {
  const obj: PermissionOperations = {}
  for (const menu of getMenuList()) {
    for (const subMenu of getSubMenuListByMenu(menu)) {
      const operations = getPermissionOperations(menu, subMenu)
      if (operations) Object.assign(obj, operations)
    }
  }
  return obj
}

//二级菜单数组(某个一级菜单下的所有二级菜单)
export const getSubMenuListByMenu = (menu: MenuType): SubMenuType[] => {
  // console.log("getSubMenuListByMenu", Object.keys(permissionMap[menu]))
  return Object.keys(permissionMap[menu]) as SubMenuType[]
}

//三级级权限数组(某个二级菜单下的所有三级权限)
export const getOpListBySubMenu = (menu: MenuType | null, subMenu: SubMenuType): string[] => {
  if (menu === null) {
    for (const menuItem of getMenuList()) {
      const operations = getPermissionOperations(menuItem, subMenu)
      if (operations) return getSortedOperationIds(operations)
    }
    return []
  }
  const operations = getPermissionOperations(menu, subMenu)
  return operations ? getSortedOperationIds(operations) : []
}

//三级级权限名称
export const getOpName = (menu: MenuType | null, subMenu: SubMenuType, op: string): string => {
  if (menu === null) {
    for (const menuItem of getMenuList()) {
      const name = getPermissionOperations(menuItem, subMenu)?.[op]
      if (name) return name
    }
    return ''
  }
  return getPermissionOperations(menu, subMenu)?.[op] ?? ''
}

//返回权限所包含的一二级菜单
export const getOpBelong = (op: string[]): [MenuType, SubMenuType, boolean][] | null => {
  const arr: [MenuType, SubMenuType, boolean][] = []
  outer: for (const menu in permissionMap) {
    const menuKey = menu as MenuType
    if (arr.some(([currentMenu]) => currentMenu === menuKey)) {
      break outer
    }
    inner: for (const subMenu in permissionMap[menuKey]) {
      const subMenuKey = subMenu as SubMenuType
      const opList = getOpListBySubMenu(menuKey, subMenuKey)
      if (arr.some(([, currentSubMenu]) => currentSubMenu === subMenuKey)) {
        break inner
      }

      if (opList.find((item) => op?.includes(item))) {
        const hasAllOps = opList.every((item) => op.includes(item))
        arr.push([menuKey, subMenuKey, hasAllOps])
      }
    }
  }
  return arr.length > 0 ? arr : null
}

export const hasPermission = (pm: string, userInfo: typeof userData): boolean => {
  const hasOpList = userInfo.permissions
  const AllOpObj = getOpList()
  //permissions返回空数组有所有权限
  const hasPermission = pm == 'hasPermission' || userInfo?.is_main || hasOpList?.find((op) => AllOpObj[op] == pm)

  if (hasPermission) {
    return true
  } else {
    return false
  }
}
